// ============================================================
// liveQueues — narrow read-only adapters wiring Operations mini-portal
// queues to their canonical owners. NO new tables, NO new endpoints:
// every loader below calls an API method that an existing sibling
// surface (PostJobs.tsx, ProjectBoard.tsx, the intake workroom, the
// Workflows surface) already uses in production.
//
// Honesty: a module id with no loader here renders the static
// registry-driven queue unchanged (OperationsMiniPortal's existing
// behavior). A module id with a loader gets a REAL, live-fetched queue
// with honest loading/error/authRequired/empty states — never a
// fabricated row.
// ============================================================

import { useEffect, useState } from 'react'
import { api, ApiError, isAuthError } from '../../../api/client'

export interface LiveQueueRow {
  id: string
  title: string
  subtitle?: string
  meta?: string
}

export interface LiveQueueState {
  rows: LiveQueueRow[]
  loading: boolean
  error: string | null
  authRequired: boolean
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

function formatStamp(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (isNaN(d.getTime())) return undefined
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function formatState(state: string): string {
  return state.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// -- Loaders — each reuses the exact API method an existing production
// surface already calls for this data. -------------------------------

async function loadClientIntakeQueue(): Promise<LiveQueueRow[]> {
  const res = await api.intake.submissions()
  return (res.submissions || []).map((row) => {
    const r = row as Record<string, unknown>
    const submitter = (r.submitter ?? {}) as Record<string, unknown>
    return {
      id: String(r.id ?? ''),
      title: String(r.subject ?? 'Untitled submission'),
      subtitle: typeof submitter.name === 'string' ? submitter.name : typeof submitter.email === 'string' ? submitter.email : 'Unknown submitter',
      meta: typeof r.status === 'string' ? r.status : undefined,
    }
  })
}

async function loadProjectBoardQueue(): Promise<LiveQueueRow[]> {
  const res = await api.commandCenter.boards.list()
  return (res.boards || []).map((b) => ({
    id: b.id,
    title: b.title,
    meta: formatStamp(b.created_at),
  }))
}

async function loadWorkflowsQueue(): Promise<LiveQueueRow[]> {
  const res = await api.workflows.listExecutions({ limit: 20 })
  return (res.executions || []).map((e) => ({
    id: e.id,
    title: e.compiled_plan?.workflow_slug || e.workflow_id,
    subtitle: `status: ${e.status}`,
    meta: formatStamp(e.started_at),
  }))
}

async function loadJobRunsQueue(): Promise<LiveQueueRow[]> {
  const res = await api.commandCenter.jobs.list()
  return (res.jobs || []).map((j) => ({
    id: j.id,
    title: j.title,
    subtitle: `state: ${formatState(j.state)}`,
    meta: j.target_surface,
  }))
}

const LIVE_QUEUE_LOADERS: Record<string, () => Promise<LiveQueueRow[]>> = {
  'client-intake': loadClientIntakeQueue,
  'project-board': loadProjectBoardQueue,
  workflows: loadWorkflowsQueue,
  'job-runs': loadJobRunsQueue,
}

/**
 * Live queue for an Operations module. Returns `undefined` for any module
 * id without a registered loader — the caller should fall back to the
 * static registry-driven queue in that case. Only the current module's
 * loader ever runs (no fan-out fetch across all modules).
 */
export function useOperationsLiveQueue(moduleId: string | undefined): LiveQueueState | undefined {
  const loader = moduleId ? LIVE_QUEUE_LOADERS[moduleId] : undefined
  const [state, setState] = useState<LiveQueueState>({ rows: [], loading: true, error: null, authRequired: false })

  useEffect(() => {
    if (!loader) return
    let cancelled = false
    setState({ rows: [], loading: true, error: null, authRequired: false })
    loader()
      .then((rows) => {
        if (cancelled) return
        setState({ rows, loading: false, error: null, authRequired: false })
      })
      .catch((err) => {
        if (cancelled) return
        if (isAuthError(err)) setState({ rows: [], loading: false, error: null, authRequired: true })
        else setState({ rows: [], loading: false, error: formatError(err), authRequired: false })
      })
    return () => {
      cancelled = true
    }
  }, [loader, moduleId])

  return loader ? state : undefined
}
